import React, { useState, useEffect } from 'react';
import ChatWindow from '../components/ChatWindow';
import MessageInput from '../components/MessageInput';
import AuthModal from '../auth/AuthModal';
import { useAuth } from '../context/AuthContext';
import { getDhammaResponse } from '../api/dhammaAgent';
import { db } from '../config/firebaseConfig';
import {
  collection, addDoc, query, orderBy, serverTimestamp, onSnapshot,
  doc, updateDoc, getDoc // setDoc is imported but not used, can remove if not needed
} from 'firebase/firestore';
import { updateUserQueryCount } from '../auth/authService'; // Correct: updateUserQueryCount is exported and used
import { auth } from '../config/firebaseConfig'; // Import auth for signOut


const HomePage = () => {
  const { currentUser, userData, loading: authLoading, userDataLoading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState('');

  // States for Session Management
  const [sessions, setSessions] = useState([]); // List of chat sessions for the user
  const [currentSessionId, setCurrentSessionId] = useState(null); // ID of the currently active session
  const [newSessionLoading, setNewSessionLoading] = useState(false); // For creating a new session

  // State for sidebar visibility, default to true (open)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // --- ADDED DEBUG LOGS ---
  console.log("HomePage Render: currentUser:", currentUser ? currentUser.email : "none");
  console.log("HomePage Render: userData:", userData);
  console.log("HomePage Render: authLoading:", authLoading, "userDataLoading:", userDataLoading);
  // --- END ADDED DEBUG LOGS ---

  // 1. Effect to load chat sessions (left panel)
  useEffect(() => {
    if (currentUser) {
      const sessionsRef = collection(db, 'chats', currentUser.uid, 'sessions');
      const q = query(sessionsRef, orderBy('lastUpdatedAt', 'desc')); // Order by most recent activity

      // --- ADDED DEBUG LOGS ---
      console.log("HomePage useEffect (sessions): Attempting to fetch sessions for UID:", currentUser.uid);
      console.log("HomePage useEffect (sessions): sessionsRef path:", sessionsRef.path);
      // --- END ADDED DEBUG LOGS ---

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedSessions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSessions(fetchedSessions);

        // --- ADDED DEBUG LOGS ---
        console.log("HomePage useEffect (sessions): Fetched sessions:", fetchedSessions);
        // --- END ADDED DEBUG LOGS ---

        // If no session is active and there are sessions, select the most recent one
        // This also handles the case where currentSessionId might be set to a deleted session ID
        if (!currentSessionId || !fetchedSessions.some(s => s.id === currentSessionId)) {
          if (fetchedSessions.length > 0) {
            setCurrentSessionId(fetchedSessions[0].id);
          } else {
            setCurrentSessionId(null); // No sessions exist
          }
        }
      }, (error) => {
        console.error("Error fetching chat sessions:", error);
        setChatError("Failed to load chat sessions."); //
      });

      return () => unsubscribe();
    } else {
      // --- ADDED DEBUG LOGS ---
      console.log("HomePage useEffect (sessions): currentUser is null, clearing sessions.");
      // --- END ADDED DEBUG LOGS ---
      setSessions([]);
      setCurrentSessionId(null);
    }
  }, [currentUser, currentSessionId]); // currentSessionId needed here to re-evaluate if it's still valid

  // 2. Effect to load messages for the current session
  useEffect(() => {
    if (currentUser && currentSessionId) {
      const messagesRef = collection(db, 'chats', currentUser.uid, 'sessions', currentSessionId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      // --- ADDED DEBUG LOGS ---
      console.log("HomePage useEffect (messages): Attempting to fetch messages for session:", currentSessionId);
      console.log("HomePage useEffect (messages): messagesRef path:", messagesRef.path);
      // --- END ADDED DEBUG LOGS ---

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedMessages = snapshot.docs.map((doc) => doc.data());
        setMessages(fetchedMessages);

        // --- ADDED DEBUG LOGS ---
        console.log("HomePage useEffect (messages): Fetched messages:", fetchedMessages);
        // --- END ADDED DEBUG LOGS ---
      }, (error) => {
        console.error("Error fetching chat messages for session:", error);
        setChatError("Failed to load chat history for this session.");
      });

      return () => unsubscribe();
    } else {
      // --- ADDED DEBUG LOGS ---
      console.log("HomePage useEffect (messages): currentUser or currentSessionId is null, clearing messages.");
      // --- END ADDED DEBUG LOGS ---
      setMessages([]); // Clear messages if no session selected or logged out
    }
  }, [currentUser, currentSessionId]); // DEPENDS ON currentSessionId

  // Function to create a new session
  const handleNewSession = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    setNewSessionLoading(true);
    setChatError('');
    try {
      const sessionsRef = collection(db, 'chats', currentUser.uid, 'sessions');
      // --- ADDED DEBUG LOGS ---
      console.log("handleNewSession: Creating session at path:", sessionsRef.path);
      // --- END ADDED DEBUG LOGS ---
      const newSessionDocRef = await addDoc(sessionsRef, {
        title: "New Chat", // Default title, will be updated with first message
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
      });
      setCurrentSessionId(newSessionDocRef.id);
      setMessages([]); // Clear messages for the new session
      // --- ADDED DEBUG LOGS ---
      console.log("handleNewSession: New session created with ID:", newSessionDocRef.id);
      // --- END ADDED DEBUG LOGS ---
    } catch (error) {
      console.error("Error creating new session:", error);
      setChatError("Failed to start a new chat. Please try again.");
    } finally {
      setNewSessionLoading(false);
    }
  };

  // Function to switch to an existing session
  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    setMessages([]); // Clear current messages, useEffect will load new ones
    // --- ADDED DEBUG LOGS ---
    console.log("handleSelectSession: Switched to session ID:", sessionId);
    // --- END ADDED DEBUG LOGS ---
  };

  const handleSendMessage = async (userMessage) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (userDataLoading) {
      setChatError("Your account data is still loading. Please wait a moment.");
      return;
    }

    // UPDATED CONDITION HERE: Check if userData is null/undefined OR if queriesLeft is missing/0
    if (!userData || typeof userData.queriesLeft === 'undefined' || userData.queriesLeft <= 0) {
      setChatError("You have reached your daily query limit or your user data is incomplete. Please upgrade your tier or wait until tomorrow.");
      return;
    }

    setIsSending(true);
    setChatError('');

    let sessionToUse = currentSessionId;
    let isNewSessionCreated = false; // Flag to check if a new session was just created

    // If no session is active, create one before sending the first message
    if (!sessionToUse) {
      try {
        const sessionsRef = collection(db, 'chats', currentUser.uid, 'sessions');
        // --- ADDED DEBUG LOGS ---
        console.log("handleSendMessage: No active session, creating new at path:", sessionsRef.path);
        // --- END ADDED DEBUG LOGS ---
        const newSessionDocRef = await addDoc(sessionsRef, {
          title: userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : ''), // Initial title from first message
          createdAt: serverTimestamp(),
          lastUpdatedAt: serverTimestamp(),
        });
        sessionToUse = newSessionDocRef.id;
        setCurrentSessionId(sessionToUse);
        isNewSessionCreated = true;
        // --- ADDED DEBUG LOGS ---
        console.log("handleSendMessage: New session created for message with ID:", newSessionDocRef.id);
        // --- END ADDED DEBUG LOGS ---
      } catch (error) {
        console.error("Error creating new session for first message:", error); //
        setChatError("Failed to start a new chat session. Please try again."); //
        setIsSending(false);
        return;
      }
    }

    // Optimistically add user message to local state for immediate display
    // Using new Date() for immediate display, onSnapshot will later update with Firestore Timestamp
    const optimisticUserMessage = { text: userMessage, sender: 'user', timestamp: new Date() };
    setMessages(prevMessages => [...prevMessages, optimisticUserMessage]);


    try {
      // Add user message to the specific session's messages subcollection
      const messageCollectionRef = collection(db, 'chats', currentUser.uid, 'sessions', sessionToUse, 'messages');
      // --- ADDED DEBUG LOGS ---
      console.log("handleSendMessage: Adding user message to path:", messageCollectionRef.path);
      // --- END ADDED DEBUG LOGS ---
      await addDoc(messageCollectionRef, {
        text: userMessage,
        sender: 'user',
        timestamp: serverTimestamp(),
      });

      // Update the session's lastUpdatedAt time and title if it was a new chat
      const sessionDocRef = doc(db, 'chats', currentUser.uid, 'sessions', sessionToUse);

      // Fetch the current session document to check its title before updating
      const currentSessionDocSnap = await getDoc(sessionDocRef); // Use getDoc to read current state
      const currentSessionData = currentSessionDocSnap.data();

      // Only update the title if it's still the default "New Chat" or if it's a freshly created session
      let sessionUpdateData = { lastUpdatedAt: serverTimestamp() };
      if (isNewSessionCreated || (currentSessionData && currentSessionData.title === "New Chat")) {
        sessionUpdateData.title = userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : '');
      }
      // --- ADDED DEBUG LOGS ---
      console.log("handleSendMessage: Updating session document:", sessionDocRef.path, "with data:", sessionUpdateData);
      // --- END ADDED DEBUG LOGS ---
      await updateDoc(sessionDocRef, sessionUpdateData);


      const updatedQueriesLeft = await updateUserQueryCount(currentUser.uid, userData.queriesLeft);
      console.log('Queries left after update:', updatedQueriesLeft);

      const aiResponse = await getDhammaResponse(userMessage);
      // --- ADDED DEBUG LOGS ---
      console.log("handleSendMessage: AI Response received:", aiResponse);
      // --- END ADDED DEBUG LOGS ---

      // Add AI message to the specific session's messages subcollection
      const aiMessageCollectionRef = collection(db, 'chats', currentUser.uid, 'sessions', sessionToUse, 'messages');
      // --- ADDED DEBUG LOGS ---
      console.log("handleSendMessage: Adding AI message to path:", aiMessageCollectionRef.path);
      // --- END ADDED DEBUG LOGS ---
      await addDoc(aiMessageCollectionRef, {
        text: aiResponse,
        sender: 'ai',
        timestamp: serverTimestamp(),
      });

      // Update session's lastUpdatedAt again after AI response
      const finalSessionUpdateRef = doc(db, 'chats', currentUser.uid, 'sessions', sessionToUse);
      // --- ADDED DEBUG LOGS ---
      console.log("handleSendMessage: Final session update for path:", finalSessionUpdateRef.path);
      // --- END ADDED DEBUG LOGS ---
      await updateDoc(finalSessionUpdateRef, {
        lastUpdatedAt: serverTimestamp(),
      });

    } catch (error) {
      console.error("Error sending message or getting AI response:", error); //
      if (error.message === "Query limit already reached.") {
        setChatError("You have reached your daily query limit. Please upgrade your tier or wait until tomorrow.");
      } else {
        setChatError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsSending(false);
    }
  };

  // Function to toggle sidebar visibility
  const handleToggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
    // --- ADDED DEBUG LOGS ---
    console.log("handleToggleSidebar: Sidebar toggled. isSidebarOpen:", !isSidebarOpen);
    // --- END ADDED DEBUG LOGS ---
  };

  // Logout function
  const handleLogout = async () => {
    // --- ADDED DEBUG LOGS ---
    console.log("handleLogout: Initiating logout for currentUser:", currentUser?.email);
    // --- END ADDED DEBUG LOGS ---
    try {
      await auth.signOut();
      setCurrentSessionId(null); // Clear current session on logout
      setMessages([]); // Clear messages on logout
      // --- ADDED DEBUG LOGS ---
      console.log("handleLogout: Logout successful.");
      // --- END ADDED DEBUG LOGS ---
    } catch (error) {
      console.error("Error logging out:", error);
      setChatError("Failed to log out. Please try again.");
    }
  };


  return (
    <div className="chat-container">
      {/* Removed the <header> element as per UI redesign */}

      <div className="chat-layout">
        {/* Sidebar */}
        <aside className={`chat-sidebar ${isSidebarOpen ? '' : 'collapsed'}`}>
          {/* Sidebar Toggle Button - positioned within the sidebar */}
          <button
            className={`sidebar-toggle ${isSidebarOpen ? '' : 'collapsed'}`}
            onClick={handleToggleSidebar}
            title={isSidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
          >
            {isSidebarOpen ? '❮' : '❯'} {/* Unicode left/right arrows */}
          </button>

          {/* Content for OPEN sidebar */}
          {isSidebarOpen && (
            <>
              {/* Dhamma AI Tutor Title (moved from header) */}
              <h1 className="sidebar-title">Dhamma AI Tutor</h1>
              <p className="subtitle">Your compassionate guide to the liberating teachings of Gautama Buddha.</p>

              <button
                onClick={handleNewSession}
                disabled={newSessionLoading || !currentUser}
                className="new-chat-button"
              >
                {newSessionLoading ? 'Creating...' : '+ New Chat'}
              </button>
              <div className="session-list">
                {sessions.length === 0 && !currentUser ? (
                  <p>Sign in to see chat history.</p>
                ) : sessions.length === 0 && currentUser ? (
                  <p>No chat sessions yet. Start a new chat!</p>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`session-item ${session.id === currentSessionId ? 'active' : ''}`}
                      onClick={() => handleSelectSession(session.id)}
                    >
                      {session.title || `Chat ${session.createdAt?.toDate().toLocaleString()}`}
                    </div>
                  ))
                )}
              </div>

              {/* User Info & Logout (moved to bottom of sidebar) */}
              <div className="sidebar-footer">
                {currentUser ? (
                  <>
                    <p className="user-email">Logged in as: {userData?.email}</p>
                    <p className="user-tier-info">
                      ({userDataLoading ? 'Loading...' : (userData?.tier || 'Sīla')} Tier: {userDataLoading ? 'Loading...' : (userData?.queriesLeft !== undefined ? userData.queriesLeft : 'N/A')} queries left today)
                    </p>
                    <button className="logout-button" onClick={handleLogout}>Logout</button>
                  </>
                ) : (
                  <button className="logout-button" onClick={() => setShowAuthModal(true)}>Sign In / Sign Up</button>
                )}
              </div>
            </>
          )}

          {/* Content for COLLAPSED sidebar (e.g., a New Chat icon) */}
          {!isSidebarOpen && currentUser && ( // Only show if collapsed AND logged in
            <div className="collapsed-sidebar-icons"> {/* New container for collapsed icons */}
              <button
                onClick={handleNewSession}
                disabled={newSessionLoading || !currentUser}
                className="new-chat-icon-button" /* New class for icon-only button */
                title="New Chat"
              >
                {/* You'll need an icon here, e.g., a plus sign SVG or Font Awesome icon */}
                +
              </button>
              {/* Potentially other icons like settings, home, etc. */}
            </div>
          )}

        </aside>

        <main className={`chat-main ${isSidebarOpen ? '' : 'sidebar-collapsed'}`}>
          {chatError && <div className="chat-error">{chatError}</div>}
          <ChatWindow messages={messages} />
          <MessageInput
            onSendMessage={handleSendMessage}
            disabled={isSending || !currentUser || userDataLoading || !userData || userData.queriesLeft <= 0}
          />
        </main>
      </div>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default HomePage;
